import { useIntl } from 'react-intl';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Rating from '@mui/material/Rating';
import Tooltip from 'components/mui/Tooltip';
import SearchCharactersWarning from 'components/mui/Layouts/components/SearchCharactersWarning';

import { SearchField, FilterChip } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/StyledComponents';
import OverflowChips from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/OverflowChips';
import type { RenderChip } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/types';

interface WorkspaceSearchInputProps {
  params: AutocompleteRenderInputParams;
  visibleChips: RenderChip[];
  hiddenChips: RenderChip[];
  thresholdWarning: boolean;
  hasActiveFilters: boolean;
  canSaveFavorite: boolean;
  onSaveFavorite: () => void;
  onClearAll: () => void;
}

// The custom "input-shell" rendered as the Autocomplete's `renderInput` target: a rectangular
// TextFieldBase whose start-adornment hosts the search icon + visible chip strip + "+N"
// overflow, and whose end-adornment hosts the below-threshold hint, the Save-to-favorite star,
// and the Clear-all control. Autocomplete drives the value + keyboard nav through
// `params.inputProps` and anchors its suggestions popper via `params.InputProps.ref`.
const WorkspaceSearchInput = ({
  params,
  visibleChips,
  hiddenChips,
  thresholdWarning,
  hasActiveFilters,
  canSaveFavorite,
  onSaveFavorite,
  onClearAll,
}: WorkspaceSearchInputProps) => {
  const { formatMessage } = useIntl();
  return (
    <SearchField
      fullWidth
      size="small"
      placeholder={formatMessage({ id: 'header.search.placeholder' })}
      slotProps={{
        htmlInput: params.inputProps,
        input: {
          ...params.InputProps,
          startAdornment: (
            <InputAdornment position="start">
              <Stack direction="row" alignItems="center" gap={0.5}>
                <SearchIcon color="action" />
                {visibleChips.map((chip) => (
                  <Tooltip key={chip.id} t={chip.title}>
                    <FilterChip
                      size="small"
                      color="primary"
                      variant="outlined"
                      avatar={chip.avatar}
                      icon={chip.icon}
                      label={chip.label}
                      onDelete={chip.onDelete}
                    />
                  </Tooltip>
                ))}
                <OverflowChips hiddenChips={hiddenChips} />
              </Stack>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction="row" alignItems="center" gap={0.5}>
                {thresholdWarning && <SearchCharactersWarning />}
                {canSaveFavorite && (
                  <Tooltip t={{ id: 'documents.tags.saveToFavorites', defaultMessage: 'Save to Favorites' }}>
                    <Rating max={1} value={null} size="small" onChange={() => onSaveFavorite()} />
                  </Tooltip>
                )}
                {hasActiveFilters && (
                  <Tooltip t={{ id: 'company.workspace.search.clearAll', defaultMessage: 'Clear all' }}>
                    <IconButton
                      size="small"
                      onClick={onClearAll}
                      aria-label={formatMessage({
                        id: 'company.workspace.search.clearAll',
                        defaultMessage: 'Clear all',
                      })}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

WorkspaceSearchInput.displayName = 'WorkspaceSearchInput';

export default WorkspaceSearchInput;
